import { hasElevatedRights, type StudioUser } from '@stratumn/studio-shared';
import { AlertTriangle, Copy, Loader2, Sparkles } from 'lucide-react';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { toast } from 'sonner';
import {
  EnvVarCellMuted,
  EnvVarCellText,
  EnvVarTable
} from '@/components/env-var/EnvVarTable';
import { Button } from '@/components/ui/button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/components/ui/hover-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  SettingHeader,
  SettingsContainer,
  SettingsContent
} from '@/components/ui/settings';
import { useCreateGeneration } from '@/hooks/queries/generations/useCreateGeneration';
import useProcess from '@/hooks/useProcess';
import useStores from '@/hooks/useStore';
import { VersionBadge } from '@/pages/Workspaces/components/VersionBadge';
import { ParamsList } from '@/routes/routes.types';
import { extractErrorMessage } from '@/services/errors/errors';
import { GenerationEnvDialog } from './GenerationEnvDialog';

// Sentinel used at the UI boundary only (shadcn Select disallows empty
// string values). Workspace IDs are UUIDs, so collision with this literal
// is impossible. The sentinel never leaves this file.
const NO_WORKSPACE_VALUE = '__no_workspace__';

type WorkspaceSelection = { kind: 'none' } | { kind: 'selected'; id: string };

export const NewGenerationPage = observer(() => {
  const process = useProcess();
  const { workspaceStore, envVarStore } = useStores();
  const navigate = useNavigate();
  const processIdParam = useParams()[ParamsList.ProcessId];
  if (!processIdParam) {
    throw new Error('Missing processId route param');
  }
  const processId = processIdParam;
  const { mutateAsync, isPending: isGenerationPending } = useCreateGeneration({
    processId
  });
  const [searchParams] = useSearchParams();
  const initialWorkspaceId = searchParams.get('workspaceId');
  const outletContext = useOutletContext<{ user: StudioUser } | undefined>();
  const isPrivileged = hasElevatedRights(outletContext?.user);

  const [selection, setSelection] = useState<WorkspaceSelection>(
    initialWorkspaceId
      ? { kind: 'selected', id: initialWorkspaceId }
      : { kind: 'none' }
  );

  const selectValue =
    selection.kind === 'none' ? NO_WORKSPACE_VALUE : selection.id;
  const handleSelectionChange = (value: string) => {
    setSelection(
      value === NO_WORKSPACE_VALUE
        ? { kind: 'none' }
        : { kind: 'selected', id: value }
    );
  };

  // Fetch workspaces on mount if applicable
  useEffect(() => {
    if (process?.draft && process.isWorkspaceFeatureEnabled) {
      workspaceStore.fetchWorkspaces(processId).catch(() => {
        // error already reported via newError → Sentry; suppress unhandled rejection
      });
    }
  }, [
    processId,
    process?.draft,
    process?.isWorkspaceFeatureEnabled,
    workspaceStore
  ]);

  // Fetch env vars on mount
  useEffect(() => {
    envVarStore.fetchEnvVars(processId).catch(() => {
      // error already reported via newError → Sentry; suppress unhandled rejection
    });
  }, [processId, envVarStore]);

  // Fetch overrides when a workspace is selected
  useEffect(() => {
    if (selection.kind === 'selected') {
      envVarStore.fetchOverrides(processId, selection.id).catch(() => {
        // error already reported via newError → Sentry; suppress unhandled rejection
      });
    }
  }, [processId, selection, envVarStore]);

  // Fetch workspace detail for published_workflow_ids
  useEffect(() => {
    if (selection.kind === 'selected') {
      workspaceStore.fetchWorkspace(processId, selection.id).catch(() => {
        // error already reported via newError → Sentry; suppress unhandled rejection
      });
    }
  }, [processId, selection, workspaceStore]);

  const workspaces = workspaceStore.getWorkspacesForProcess(processId);

  const workflows = process?.workflows ?? [];

  // Get published workflow IDs from workspace detail
  const publishedWorkflowIds: Record<string, string> = useMemo(() => {
    if (selection.kind === 'none' || !workspaceStore.currentWorkspace) {
      return {};
    }
    return workspaceStore.currentWorkspace.published_workflow_ids ?? {};
  }, [selection, workspaceStore.currentWorkspace]);

  // Resolve env vars with overrides.
  // Note: no useMemo -- observer() tracks MobX observable access (envVars, overrides Map)
  // and re-renders when they change. useMemo would prevent this since the Map reference is stable.
  const resolvedEnvVars = (() => {
    const overrides =
      selection.kind === 'selected'
        ? envVarStore.getOverridesForWorkspace(selection.id)
        : [];

    // Build a lookup once: O(M) — instead of O(N×M) `overrides.find` per var.
    // Still inside render so MobX tracks `overrides` reactivity.
    const overridesById = new Map(overrides.map(o => [o.envVarId, o]));

    const resolved: {
      name: string;
      value: string;
      source: 'process' | 'workspace';
    }[] = [];

    for (const v of envVarStore.envVars) {
      const override = overridesById.get(v.id);
      const enabled = override ? override.enabled : v.enabled;
      const value = override ? override.value : v.value;
      const source: 'process' | 'workspace' = override
        ? 'workspace'
        : 'process';
      if (enabled) {
        resolved.push({ name: v.name, value, source });
      }
    }

    return resolved;
  })();

  const generability = process?.isProcessGenerable;

  // Additional check: without a workspace, workflows need published IDs
  const workspaceSpecificReasons: string[] = (() => {
    if (selection.kind === 'selected' || !process?.draft) {
      return [];
    }
    const missingPublishedId = (process?.workflows ?? []).filter(
      wf => !wf.published_id
    );
    return missingPublishedId.map(
      wf =>
        `Workflow "${wf.name}" has no published ID (select a workspace or publish it manually)`
    );
  })();

  const allReasons = [
    ...(generability?.reasons ?? []),
    ...workspaceSpecificReasons
  ];
  const canGenerate =
    (generability?.generable ?? true) && workspaceSpecificReasons.length === 0;

  const handleGenerate = async () => {
    try {
      const wsId = selection.kind === 'selected' ? selection.id : undefined;
      const generation = await mutateAsync({ workspaceId: wsId });
      navigate(`/${processId}/generation/${generation.id}`);
    } catch (error) {
      const message = extractErrorMessage(error);
      toast.error(`Generation failed: ${message}`);
    }
  };

  const [envDialogOpen, setEnvDialogOpen] = useState(false);

  const showWorkspaceCard = process?.draft && process.isWorkspaceFeatureEnabled;

  return (
    <SettingsContainer>
      <SettingHeader
        description="Configure and trigger a new generation"
        title="New Generation"
      />
      <SettingsContent className="gap-6">
        {showWorkspaceCard && (
          <div className="rounded-xl bg-card p-6 shadow-verail">
            <h2 className="mb-1 font-semibold text-lg">Workspace</h2>
            <p className="mb-4 text-muted-foreground text-sm">
              Select a workspace for this generation
            </p>
            <div className="flex items-center gap-6">
              <Select onValueChange={handleSelectionChange} value={selectValue}>
                <SelectTrigger className="w-full max-w-sm">
                  <SelectValue placeholder="No workspace (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_WORKSPACE_VALUE}>
                    No workspace (default)
                  </SelectItem>
                  {workspaces.map(ws => (
                    <SelectItem key={ws.id} value={ws.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded-full"
                          style={{ backgroundColor: ws.color || '#6366f1' }}
                        />
                        {ws.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selection.kind === 'selected' &&
                workspaceStore.currentWorkspace && (
                  <div className="flex items-center gap-3 text-sm">
                    <VersionBadge
                      version={workspaceStore.currentWorkspace.version}
                    />
                    {workspaceStore.currentWorkspace.version && (
                      <span className="font-mono text-muted-foreground text-xs">
                        {workspaceStore.currentWorkspace.version.name}
                      </span>
                    )}
                  </div>
                )}
            </div>
          </div>
        )}

        <div className="rounded-xl bg-card p-6 shadow-verail">
          <h2 className="mb-1 font-semibold text-lg">Workflows</h2>
          <p className="mb-4 text-muted-foreground text-sm">
            Workflows included in this generation
          </p>
          <EnvVarTable
            headers={[
              { label: 'Name' },
              { label: 'Env Variable' },
              { label: 'Published ID' }
            ]}
          >
            {workflows.map(wf => (
              <tr key={wf.id}>
                <EnvVarCellText>{wf.name}</EnvVarCellText>
                <EnvVarCellText>
                  {wf.id_environment_variable_name || (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </EnvVarCellText>
                <EnvVarCellMuted>
                  {selection.kind === 'selected'
                    ? publishedWorkflowIds[wf.id] || 'Not set'
                    : wf.published_id || 'Not set'}
                </EnvVarCellMuted>
              </tr>
            ))}
          </EnvVarTable>
        </div>

        {resolvedEnvVars.length > 0 && (
          <div className="rounded-xl bg-card p-6 shadow-verail">
            <h2 className="mb-1 font-semibold text-lg">
              Environment Variables
            </h2>
            <p className="mb-4 text-muted-foreground text-sm">
              Values that will be provided during the generation
            </p>
            <EnvVarTable
              headers={[
                { label: 'Name' },
                { label: 'Value' },
                { label: 'Source' }
              ]}
            >
              {resolvedEnvVars.map(v => (
                <tr key={v.name}>
                  <EnvVarCellText>{v.name}</EnvVarCellText>
                  <EnvVarCellMuted>{v.value}</EnvVarCellMuted>
                  <EnvVarCellText>
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs ${
                        v.source === 'workspace'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {v.source}
                    </span>
                  </EnvVarCellText>
                </tr>
              ))}
            </EnvVarTable>
          </div>
        )}
      </SettingsContent>

      <div className="sticky bottom-0 z-10 mt-auto w-full border-gray-200 border-t border-solid bg-background">
        <div className="mx-auto flex w-full max-w-7xl justify-end gap-2 px-8 py-4">
          {isPrivileged && (
            <>
              <Button onClick={() => setEnvDialogOpen(true)} variant="outline">
                <Copy className="mr-2 h-4 w-4" />
                Copy Env
              </Button>
              <GenerationEnvDialog
                onOpenChange={setEnvDialogOpen}
                open={envDialogOpen}
                processId={processId}
                workspaceId={
                  selection.kind === 'selected' ? selection.id : undefined
                }
              />
            </>
          )}
          <HoverCard closeDelay={100} openDelay={0}>
            <HoverCardTrigger asChild>
              <span
                className={`inline-flex ${canGenerate ? '' : 'cursor-not-allowed'}`}
              >
                <Button
                  disabled={!canGenerate || isGenerationPending}
                  onClick={handleGenerate}
                >
                  {isGenerationPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  {selection.kind === 'selected'
                    ? 'Generate with workspace'
                    : 'Generate'}
                </Button>
              </span>
            </HoverCardTrigger>
            {!canGenerate && allReasons.length > 0 && (
              <HoverCardContent
                className="w-auto max-w-xs p-3"
                side="bottom"
                sideOffset={8}
              >
                {allReasons.length === 1 ? (
                  <p className="flex items-start gap-2 text-muted-foreground text-sm">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                    {allReasons[0]}
                  </p>
                ) : (
                  <div className="flex items-start gap-2 text-muted-foreground text-sm">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                    <ul className="list-disc pl-4">
                      {allReasons.map(reason => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </HoverCardContent>
            )}
          </HoverCard>
        </div>
      </div>
    </SettingsContainer>
  );
});
